import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { MicrosoftExcelLogo } from "@phosphor-icons/react";
import api, { money, num } from "../lib/api";
import { DataTable } from "./DataTable";
import { TableRow, TableCell } from "./ui/table";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { itemColumns } from "./itemColumns";

const ITEM_METRICS = ["items", "inventory"];

const AMOUNT_LABEL = { revenue: "Paid Amount", receivables: "Remaining", payables: "Remaining", inventory: "Stock Value", items: "Stock Value", customers: "Balance", vendors: "Balance", cash: "Amount", projected: "Inflow / Outflow" };
const TOTAL_LABEL = { customers: "Balance", vendors: "Balance", items: "Unit Cost", inventory: "Unit Cost" };

function buildColumns(metric, mode) {
  const cols = [
    { key: "doc_no", header: "No.", filter: "text", cell: (r) => <span className="font-mono text-xs font-semibold text-primary" data-testid="drill-row-no">{r.doc_no || "—"}</span> },
    { key: "type", header: "Type", filter: "select" },
    { key: "party", header: ["customers", "vendors", "items", "inventory"].includes(metric) ? "Name" : "Customer / Vendor", filter: "text" },
    { key: "date", header: "Date" },
    { key: "due_date", header: "Due Date" },
    { key: "status", header: "Status", filter: "select" },
  ];
  if (!["customers", "vendors"].includes(metric)) cols.push({ key: "total", header: TOTAL_LABEL[metric] || "Total", numeric: true, align: "right", cell: (r) => <span className="tabular">{money(r.total)}</span> });
  cols.push({ key: "amount", header: AMOUNT_LABEL[metric] || "Amount", numeric: true, align: "right", cell: (r) => <span className={`tabular font-semibold ${r.amount < 0 ? "text-red-600" : ""}`}>{money(r.amount)}</span> });
  if (mode === "projected") cols.push({ key: "running", header: "Projected Balance", numeric: true, align: "right", cell: (r) => <span className={`tabular font-bold ${r.running < 0 ? "text-red-600" : "text-primary"}`} data-testid="drill-running">{money(r.running)}</span> });
  return cols;
}

function exportRows(rows, cols, title) {
  const aoa = [cols.map((c) => c.header), ...rows.map((r) => cols.map((c) => (c.accessor ? c.accessor(r) : r[c.key]) ?? ""))];
  const amtIdx = cols.findIndex((c) => c.key === "amount");
  aoa.push(cols.map((_, i) => (i === 0 ? "Total" : i === amtIdx ? Math.round(rows.reduce((s, r) => s + Number(r.amount || 0), 0) * 100) / 100 : "")));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), title.slice(0, 30));
  XLSX.writeFile(wb, `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

export function DashboardDrilldown({ metric, onClose }) {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["drilldown", metric],
    queryFn: async () => (await api.get(`/dashboard/drilldown/${metric}`)).data,
    enabled: !!metric, staleTime: 0,
  });
  const mode = data?.mode;
  const isItems = ITEM_METRICS.includes(metric);
  const { data: items = [], isLoading: itemsLoading } = useQuery({ queryKey: ["items"], queryFn: async () => (await api.get("/items")).data, enabled: isItems });
  const openRow = (r) => { onClose(); navigate(r.link); };
  const cols = isItems
    ? [...itemColumns(openRow), { key: "amount", header: "Stock Value", numeric: true, align: "right", cell: (r) => <span className="tabular font-semibold">{money(r.amount)}</span> }]
    : buildColumns(metric, mode);
  const rows = isItems
    ? items.map((i) => ({ ...i, _k: i.id, link: `/inventory/item/${i.id}`, amount: Math.round(Number(i.quantity || 0) * Number(i.cost || 0) * 100) / 100 }))
    : (data?.rows || []).map((r, i) => ({ ...r, _k: i }));
  const footer = (view) => view.length > 0 && mode !== "projected" && (
    <TableRow className="bg-secondary/40 font-bold">
      <TableCell colSpan={cols.length - 1} className="text-right text-xs uppercase tracking-wide">{mode === "count" ? `${num(view.length)} records · total` : "Total"}</TableCell>
      <TableCell className="text-right tabular" data-testid="drill-total">{money(view.reduce((s, r) => s + Number(r.amount || 0), 0))}</TableCell>
    </TableRow>
  );
  const headline = !data ? "" : mode === "count" ? `${num(data.count)} records` : money(data.total);
  return (
    <Sheet open={!!metric} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className={`w-full ${isItems ? "sm:max-w-[95vw]" : "sm:max-w-5xl"} overflow-y-auto`} data-testid="dashboard-drilldown">
        <SheetHeader>
          <SheetTitle data-testid="drill-title">{data?.title || "Loading…"}</SheetTitle>
          <SheetDescription>{mode === "projected" ? "Cash on hand, then every open receivable (+) and payable (−) by due date, with the running projected balance." : "The records behind this dashboard figure. Click a row to open it."}</SheetDescription>
          {data && <p className="text-2xl font-black tabular text-primary" data-testid="drill-headline">{headline}{mode === "projected" ? " projected" : ""}</p>}
        </SheetHeader>
        <div className="mt-4 overflow-x-auto">
          <DataTable
            data={rows} columns={cols} rowKey={(r) => r._k} rowTestid="drill-row" testid="drill-table"
            loading={isLoading || (isItems && itemsLoading)} footer={footer} empty="No records behind this figure." searchPlaceholder="Search…"
            rowProps={(r) => ({ onClick: () => openRow(r), className: "cursor-pointer" })}
            toolbarExtra={(view) => (
              <Button size="sm" variant="outline" disabled={view.length === 0} onClick={() => exportRows(view, cols, data?.title || "export")} className="h-8 rounded-sm text-xs gap-1.5" data-testid="drill-export">
                <MicrosoftExcelLogo size={15} /> Export to Excel
              </Button>
            )}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
